import {
  DataPolicyColumnEffect,
  DataPolicyOperator,
  DataPolicyRowEffect,
  TableColumnInput,
  TableDefinition,
} from '@vendia/management-api-types'
import { ListOption } from 'src/components/inputs/listbox.input'

export const rowPermissionOptions: ListOption[] = [
  {
    value: DataPolicyRowEffect.Exclude,
    label: 'Exclude',
    description: 'Exclude this row from the table',
  },
  {
    value: DataPolicyRowEffect.Allow,
    label: 'Allow',
    description: 'Include this row in the table',
  },
]

export function getColumnPermissionOptions(defaultEffect?: DataPolicyColumnEffect): ListOption[] {
  const excludeAll = defaultEffect === DataPolicyColumnEffect.Exclude
  const allowAll = defaultEffect === DataPolicyColumnEffect.Allow

  return [
    {
      value: DataPolicyColumnEffect.Exclude,
      label: 'Exclude',
      description: excludeAll ? 'All columns are excluded by default' : 'Exclude this column from the table',
      disabled: excludeAll,
    },
    {
      value: DataPolicyColumnEffect.Allow,
      label: 'Allow',
      description: allowAll ? 'All columns are allowed by default' : 'Include this column in the table',
      disabled: allowAll,
    },
    {
      value: DataPolicyColumnEffect.Mask,
      label: 'Mask entire value',
      description: 'Replace the entire value with *',
    },
    {
      value: DataPolicyColumnEffect.MaskExceptLast,
      label: 'Mask everything except the last',
      description: 'Replace all but the last number of characters provided with *',
    },
    {
      value: DataPolicyColumnEffect.Replace,
      label: 'Replace entire value with',
      description: 'Replace the entire value with the provided value',
    },
  ]
}

export const getPolicyEffectLabel = (
  effect: any,
  defaultColumnPolicyEffect?: DataPolicyColumnEffect,
): string | undefined => {
  const options = getColumnPermissionOptions(defaultColumnPolicyEffect)
  return options.find((option) => option.value === effect)?.label as string
}

export const dataProductOperatorOptions: ListOption[] = [
  {
    value: DataPolicyOperator.Equals,
    label: 'Equal to',
  },
  {
    value: DataPolicyOperator.GreaterThan,
    label: 'Greater than',
  },
  {
    value: DataPolicyOperator.GreaterThanOrEqual,
    label: 'Greater than or equal to',
  },
  {
    value: DataPolicyOperator.In,
    label: 'In',
  },
  {
    value: DataPolicyOperator.IsNotNull,
    label: 'Not null',
  },
  {
    value: DataPolicyOperator.IsNull,
    label: 'Null',
  },
  {
    value: DataPolicyOperator.LessThan,
    label: 'Less than',
  },
  {
    value: DataPolicyOperator.LessThanOrEqual,
    label: 'Less than or equal to',
  },
  {
    value: DataPolicyOperator.Like,
    label: 'Like',
  },
  {
    value: DataPolicyOperator.NotEquals,
    label: 'Not equal to',
  },
  {
    value: DataPolicyOperator.NotIn,
    label: 'Not in',
  },
  {
    value: DataPolicyOperator.NotLike,
    label: 'Not like',
  },
]

export const getOperatorLabel = (operator?: DataPolicyOperator) =>
  dataProductOperatorOptions.find((option) => option.value === operator)?.label as string

export function getColumnOptions(columns: TableColumnInput[] = [], policies: any[] = []): ListOption[] {
  const configuredColumns = policies?.map((policy) => policy.name)

  return columns.map((column) => {
    const disabled = configuredColumns.includes(column.name)

    // Extract just the type key if column.type is a JSON string
    let columnType = column.type
    if (typeof column.type === 'string' && column.type.startsWith('{')) {
      try {
        const parsed = JSON.parse(column.type)
        columnType = Object.keys(parsed)[0].toUpperCase()
      } catch {
        // If parsing fails, use the original type
        columnType = column.type
      }
    }

    const description = disabled
      ? 'Column has been configured'
      : `${columnType}${column.nullable ? '' : ' (not nullable)'}`

    return {
      value: column.name,
      label: column.name,
      description,
      disabled,
    }
  })
}

export const isAllAllowed = ({ columnPolicies, defaultColumnPolicyEffect }: TableDefinition) =>
  columnPolicies?.every((p) => p.effect === DataPolicyColumnEffect.Allow) ||
  (columnPolicies.length === 0 && defaultColumnPolicyEffect === DataPolicyColumnEffect.Allow)

export const isAllExcluded = ({ columnPolicies }: TableDefinition) =>
  columnPolicies?.every((p) => p.effect === DataPolicyColumnEffect.Exclude)
